import { Bell, History, Pin, Search, Sprout, Briefcase, Trash2 } from 'lucide-react';

export function BellIcon() {
  return <Bell size={16} aria-hidden />;
}

export function HistoryIcon() {
  return <History size={16} aria-hidden />;
}

/** The Pinned Chats section header glyph. */
export function PinIcon() {
  return <Pin size={16} aria-hidden />;
}

export function SearchGlyph() {
  return <Search size={16} aria-hidden />;
}

export function GrowthGoalsIcon() {
  return <Sprout size={16} aria-hidden />;
}

export function AgentsAtWorkIcon() {
  return <Briefcase size={16} aria-hidden />;
}

export function TrashIcon() {
  return <Trash2 size={16} aria-hidden />;
}

/**
 * "Join Symphony community" — the DS `Groups` mark, exported unchanged from Figma `Sidebar-Menu`
 * node 2679:3313, on its own 18-unit box.
 *
 * Inlined rather than taken from lucide because the set has nothing that IS this mark: `Users` and
 * `UsersRound` are OUTLINE two-person marks, where this is a SOLID three-person group — a different
 * drawing, not a different size. Shared by the wide rail and the mobile drawer through
 * `CommunityLink`, and by the phones as the same export (iOS `symphony-groups` imageset, Android
 * `ic_symphony_groups`), so one glyph reads the same on all three surfaces.
 *
 * **It renders at 20, where every neighbouring row's glyph is 16 — deliberately, by design decision,
 * not by accident.** Two consequences worth knowing before "fixing" either of them:
 *
 *   - Its ink runs ~17.8px against a lucide row's ~13.3px (Figma draws this mark edge to edge — 16 of
 *     its 18 units — where lucide leaves 2 units of padding on a 24 grid). So it reads noticeably
 *     heavier than Home or Chat. That is the intent.
 *   - The icon BOXES are unchanged (24 on the rail, the shared 20px `.rowIcon` slot in the drawer), so
 *     every label in the menu still starts at the same x. The bigger glyph eats its own slack, which
 *     leaves ~2px less air between this mark and its label than a 16px row has. Widening the box to
 *     buy that back would push this one label out of the column, which is the worse of the two.
 */
export function CommunityIcon({ size = 20 }: { size?: number } = {}) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" aria-hidden>
      <path
        d="M9.01685 3.5C10.4231 3.50024 11.5634 4.67274 11.5634 6.11905C11.5634 6.76631 11.333 7.35721 10.9546 7.81427C11.4522 8.15634 11.8713 8.59499 12.1951 9.03683C12.9144 10.0184 13.4111 11.3127 13.4744 12.6421C13.5247 13.7007 12.69 14.4995 11.7505 14.5H6.0445C5.10377 14.5 4.26462 13.698 4.32157 12.6329L4.34146 12.3761C4.46791 11.0979 5.01694 9.89447 5.73612 8.98875C6.08542 8.5489 6.53009 8.11488 7.04821 7.78051C6.68702 7.32842 6.47025 6.74985 6.47025 6.11905C6.47025 4.67259 7.6104 3.5 9.01685 3.5ZM5.03879 4.39007C5.30224 4.39007 5.55371 4.44282 5.78486 4.53637C5.56218 5.01562 5.43769 5.55247 5.43769 6.11905C5.43769 6.13373 5.43852 6.14839 5.43868 6.16304C5.34504 6.04114 5.20157 5.9615 5.03879 5.9615C4.75653 5.96165 4.52748 6.19702 4.52748 6.48735C4.52748 6.77768 4.75653 7.01306 5.03879 7.01321C5.23399 7.01321 5.40127 6.8993 5.48743 6.73391C5.54268 7.07126 5.64273 7.39306 5.78088 7.69252C5.47178 7.9848 5.17564 8.31046 4.89853 8.66341C3.78286 8.84603 2.628 10.3636 2.529 11.8902C2.52155 12.0055 2.61261 12.0997 2.72497 12.0999H3.1378C2.98814 12.6151 2.88181 13.1418 2.82644 13.6713H2.72497C1.78981 13.6711 0.933891 12.8695 1.00403 11.7858L1.02393 11.5648C1.14442 10.4625 1.62212 9.4325 2.24251 8.6583C2.53149 8.29773 2.9023 7.93363 3.33874 7.64546C3.12461 7.3136 2.99953 6.91548 2.99953 6.48735C2.99953 5.32915 3.91266 4.39022 5.03879 4.39007ZM12.9571 4.39007C14.0834 4.39007 14.9964 5.32905 14.9964 6.48735C14.9964 6.88878 14.8842 7.26222 14.694 7.58101C15.1789 7.89013 15.5785 8.30197 15.8777 8.70638C16.5002 9.54777 16.9311 10.655 16.9968 11.7981C17.0586 12.8749 16.2074 13.6713 15.2739 13.6713H14.9735C14.9242 13.1418 14.8292 12.6152 14.695 12.0999H15.2739C15.3722 12.0999 15.4551 12.0282 15.4699 11.9331L15.4719 11.8902C15.3793 10.2828 14.2451 8.68664 13.0814 8.64704C12.8223 8.29076 12.543 7.96287 12.2498 7.66899C12.378 7.38622 12.4711 7.08381 12.5264 6.76767C12.617 6.91466 12.7754 7.01311 12.9571 7.01321C13.2395 7.01321 13.4684 6.77777 13.4684 6.48735C13.4684 6.19693 13.2395 5.9615 12.9571 5.9615C12.8091 5.96157 12.6764 6.02691 12.5831 6.1303C12.5831 6.12655 12.5841 6.1228 12.5841 6.11905C12.5841 5.54915 12.458 5.00956 12.2329 4.52818C12.4581 4.44007 12.7019 4.3901 12.9571 4.39007ZM8.99198 8.7381C7.55089 8.73836 5.95434 10.7291 5.84753 12.7188C5.84134 12.8344 5.932 12.9285 6.0445 12.9286H11.7505L11.7912 12.9245C11.8704 12.9076 11.9332 12.8432 11.9464 12.7618L11.9484 12.7188C11.8536 10.7292 10.433 8.73849 8.99198 8.7381ZM9.01685 5.07143C8.45427 5.07143 7.99821 5.54046 7.99821 6.11905C7.99821 6.66146 8.39876 7.10789 8.9124 7.16155L9.01685 7.16667L9.1203 7.16155C9.63409 7.10804 10.0355 6.66157 10.0355 6.11905C10.0355 5.54061 9.57923 5.07167 9.01685 5.07143Z"
        fill="currentColor"
      />
    </svg>
  );
}
